import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Lock, LogOut, RefreshCw, ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'alas_admin_token';

const inputCls =
  'w-full border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm font-light text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-gold/70';

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      data-testid="admin-login-form"
      onSubmit={submit}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      className="w-full max-w-md border border-white/10 bg-surface p-10"
    >
      <div className="flex h-12 w-12 items-center justify-center border border-gold/25">
        <Lock className="h-5 w-5 text-gold" strokeWidth={1.25} />
      </div>
      <h1 className="font-display mt-6 text-3xl tracking-tight text-white">Concierge Access</h1>
      <p className="mt-2 text-sm font-light text-white/50">Sign in to view reservation requests.</p>
      <div className="mt-8 space-y-5">
        <div>
          <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-gold/80">Email</span>
          <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={inputCls} />
        </div>
        <div>
          <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-gold/80">Password</span>
          <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
        </div>
      </div>
      {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">{error}</p>}
      <button
        data-testid="admin-login-submit"
        type="submit"
        disabled={loading}
        className="mt-8 w-full bg-gold py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-bright disabled:opacity-60"
      >
        {loading ? 'Signing in…' : 'Sign In'}
      </button>
    </motion.form>
  );
};

const STATUS_STYLES = {
  pending: 'border-gold/30 bg-gold/10 text-gold',
  new: 'border-gold/30 bg-gold/10 text-gold',
  confirmed: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
  accepted: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
  declined: 'border-red-400/30 bg-red-400/10 text-red-300',
};

const PhotoshootList = ({ token, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/photoshoot-bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const { data } = await axios.patch(`${API}/photoshoot-bookings/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setBusy('');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this photoshoot request permanently?')) return;
    setBusy(id + 'delete');
    try {
      await axios.delete(`${API}/photoshoot-bookings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings((bs) => bs.filter((b) => b.id !== id));
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setBusy('');
    }
  };

  if (loading) return <p className="text-sm text-white/40">Loading…</p>;
  if (!bookings.length) return <div data-testid="photoshoot-empty" className="border border-white/10 bg-surface p-16 text-center text-white/40">No photoshoot requests yet.</div>;

  return (
    <div className="space-y-4">
      {bookings.map((b) => (
        <motion.article
          key={b.id}
          data-testid={`photoshoot-row-${b.id}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-white/10 bg-surface p-6 transition-colors duration-300 hover:border-gold/30"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-xl text-white">{b.full_name}</h3>
              <p className="mt-1 text-sm text-gold">{b.vehicle} · {b.duration}</p>
            </div>
            <div className="text-right">
              <span data-testid={`photoshoot-status-${b.id}`} className={`border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[b.status] || STATUS_STYLES.pending}`}>{b.status}</span>
              <p className="mt-2 text-xs text-white/40">{new Date(b.created_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="mt-4 grid gap-3 border-t border-white/10 pt-4 text-sm text-white/70 sm:grid-cols-2 lg:grid-cols-4">
            <span className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gold" /> {b.phone}</span>
            <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold" /> {b.email}</span>
            <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-gold" /> {b.shoot_location}</span>
            <span className="text-white/70">{b.preferred_date} · {b.start_time}</span>
          </div>
          <div className="mt-3 space-y-1 text-sm font-light text-white/50">
            <p>Shoot: {b.shoot_type} · {b.num_people} people · Stationary: {b.stationary} · Reposition: {b.repositioned}</p>
            {b.notes && <p>Notes: {b.notes}</p>}
          </div>
          <div className="mt-4 flex gap-3 border-t border-white/10 pt-4">
            <button
              data-testid={`photoshoot-confirm-${b.id}`}
              onClick={() => setStatus(b.id, 'confirmed')}
              disabled={busy === b.id + 'confirmed' || b.status === 'confirmed'}
              className="border border-emerald-400/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300 transition-colors duration-300 hover:bg-emerald-400/10 disabled:opacity-30"
            >
              {busy === b.id + 'confirmed' ? 'Saving…' : 'Confirm'}
            </button>
            <button
              data-testid={`photoshoot-decline-${b.id}`}
              onClick={() => setStatus(b.id, 'declined')}
              disabled={busy === b.id + 'declined' || b.status === 'declined'}
              className="border border-red-400/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-red-300 transition-colors duration-300 hover:bg-red-400/10 disabled:opacity-30"
            >
              {busy === b.id + 'declined' ? 'Saving…' : 'Decline'}
            </button>
            <button
              data-testid={`photoshoot-delete-${b.id}`}
              onClick={() => remove(b.id)}
              disabled={busy === b.id + 'delete'}
              className="ml-auto px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] text-white/30 transition-colors duration-300 hover:text-red-400 disabled:opacity-30"
            >
              {busy === b.id + 'delete' ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </motion.article>
      ))}
    </div>
  );
};

const PartnerList = ({ token, onLogout }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner-applications`, { headers: { Authorization: `Bearer ${token}` } });
      setApps(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Delete this application permanently?')) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/partner-applications/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setApps((as) => as.filter((a) => a.id !== id));
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setBusy('');
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const { data } = await axios.patch(
        `${API}/partner-applications/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApps((as) => as.map((a) => (a.id === id ? data : a)));
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setBusy('');
    }
  };

  if (loading) return <p className="text-sm text-white/40">Loading…</p>;
  if (!apps.length) return <div data-testid="partner-apps-empty" className="border border-white/10 bg-surface p-16 text-center text-white/40">No partner applications yet.</div>;

  return (
    <div className="space-y-4">
      {apps.map((a) => (
        <motion.article
          key={a.id}
          data-testid={`partner-app-row-${a.id}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-white/10 bg-surface p-6 transition-colors duration-300 hover:border-gold/30"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-xl text-white">{a.full_name}</h3>
              <p className="mt-1 text-sm text-gold">{a.vehicle_year} {a.vehicle_make} {a.vehicle_model} · {a.mileage} mi</p>
            </div>
            <div className="text-right">
              <span className="border border-gold/30 bg-gold/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold">{a.status}</span>
              <p className="mt-2 text-xs text-white/40">{new Date(a.created_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="mt-4 grid gap-3 border-t border-white/10 pt-4 text-sm text-white/70 sm:grid-cols-2 lg:grid-cols-4">
            <span className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gold" /> {a.phone}</span>
            <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold" /> {a.email}</span>
            <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-gold" /> {a.vehicle_location}</span>
            <span className="text-white/70 capitalize">{a.financed_or_leased}{a.monthly_payment ? ` · $${a.monthly_payment}/mo` : ''}</span>
          </div>
          {a.additional_info && <p className="mt-3 text-sm font-light text-white/50">Notes: {a.additional_info}</p>}
          {a.photos && a.photos.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {a.photos.map((p, i) => (
                <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer">
                  <img data-testid={`partner-app-photo-${a.id}-${i}`} src={`${API}/files/${p}`} alt={`Vehicle photo ${i + 1}`} loading="lazy" className="h-20 w-28 border border-white/10 object-cover transition-colors hover:border-gold/50" />
                </a>
              ))}
            </div>
          )}
          <div className="mt-4 flex gap-3 border-t border-white/10 pt-4">
            <button
              data-testid={`partner-app-accept-${a.id}`}
              onClick={() => setStatus(a.id, 'accepted')}
              disabled={busy === a.id + 'accepted' || a.status === 'accepted'}
              className="border border-emerald-400/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300 transition-colors duration-300 hover:bg-emerald-400/10 disabled:opacity-30"
            >
              {busy === a.id + 'accepted' ? 'Saving…' : 'Accept'}
            </button>
            <button
              data-testid={`partner-app-decline-${a.id}`}
              onClick={() => setStatus(a.id, 'declined')}
              disabled={busy === a.id + 'declined' || a.status === 'declined'}
              className="border border-red-400/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-red-300 transition-colors duration-300 hover:bg-red-400/10 disabled:opacity-30"
            >
              {busy === a.id + 'declined' ? 'Saving…' : 'Decline'}
            </button>
            <button
              data-testid={`partner-app-delete-${a.id}`}
              onClick={() => remove(a.id)}
              disabled={busy === a.id}
              className="ml-auto px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] text-white/30 transition-colors duration-300 hover:text-red-400 disabled:opacity-30"
            >
              {busy === a.id ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </motion.article>
      ))}
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('reservations');

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const { data } = await axios.patch(
        `${API}/reservations/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setBusy('');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this reservation request permanently?')) return;
    setBusy(id + 'delete');
    try {
      await axios.delete(`${API}/reservations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setBusy('');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/reservations`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError('Could not load reservations.');
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-dashboard" className="w-full max-w-6xl">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium uppercase tracking-[0.35em] text-gold">ALAS Velocity</span>
          <h1 className="font-display mt-2 text-4xl tracking-tight text-white">Concierge Dashboard</h1>
          <div className="mt-4 flex gap-6">
            <button
              data-testid="tab-reservations"
              onClick={() => setTab('reservations')}
              className={`pb-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${tab === 'reservations' ? 'border-b-2 border-gold text-gold' : 'text-white/40 hover:text-white'}`}
            >
              Reservations ({rows.length})
            </button>
            <button
              data-testid="tab-partners"
              onClick={() => setTab('partners')}
              className={`pb-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${tab === 'partners' ? 'border-b-2 border-gold text-gold' : 'text-white/40 hover:text-white'}`}
            >
              Partner Applications
            </button>
            <button
              data-testid="tab-photoshoots"
              onClick={() => setTab('photoshoots')}
              className={`pb-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${tab === 'photoshoots' ? 'border-b-2 border-gold text-gold' : 'text-white/40 hover:text-white'}`}
            >
              Photoshoots
            </button>
          </div>
        </div>
        <div className="flex gap-3">
          <button data-testid="admin-refresh-button" onClick={load} className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/80 transition-colors hover:border-gold hover:text-gold">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button data-testid="admin-logout-button" onClick={onLogout} className="flex items-center gap-2 border border-gold/50 px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/10">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </div>

      {tab === 'partners' && <PartnerList token={token} onLogout={onLogout} />}
      {tab === 'photoshoots' && <PhotoshootList token={token} onLogout={onLogout} />}

      {tab === 'reservations' && loading && <p className="text-sm text-white/40">Loading…</p>}
      {tab === 'reservations' && error && <p data-testid="admin-error" className="text-sm text-red-400">{error}</p>}
      {tab === 'reservations' && !loading && !error && rows.length === 0 && (
        <div data-testid="admin-empty" className="border border-white/10 bg-surface p-16 text-center text-white/40">
          No reservation requests yet.
        </div>
      )}

      {tab === 'reservations' && (
      <div className="space-y-4">
        {rows.map((r) => (
          <motion.article
            key={r.id}
            data-testid={`reservation-row-${r.id}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-white/10 bg-surface p-6 transition-colors duration-300 hover:border-gold/30"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-display text-xl text-white">{r.full_name}</h3>
                <p className="mt-1 text-sm text-gold">{r.vehicle}</p>
              </div>
              <div className="text-right">
                <span data-testid={`reservation-status-${r.id}`} className={`border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>{r.status}</span>
                <p className="mt-2 text-xs text-white/40">{new Date(r.created_at).toLocaleString()}</p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 border-t border-white/10 pt-4 text-sm text-white/70 sm:grid-cols-2 lg:grid-cols-4">
              <span className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gold" /> {r.phone}</span>
              <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold" /> {r.email}</span>
              <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-gold" /> {r.pickup_location}</span>
              <span className="text-white/70">{r.pickup_date} → {r.return_date}</span>
            </div>
            {(r.delivery_address || r.special_requests) && (
              <div className="mt-3 space-y-1 text-sm font-light text-white/50">
                {r.delivery_address && <p>Delivery: {r.delivery_address}</p>}
                {r.special_requests && <p>Requests: {r.special_requests}</p>}
              </div>
            )}
            <div className="mt-4 flex gap-3 border-t border-white/10 pt-4">
              <button
                data-testid={`reservation-confirm-${r.id}`}
                onClick={() => setStatus(r.id, 'confirmed')}
                disabled={busy === r.id + 'confirmed' || r.status === 'confirmed'}
                className="border border-emerald-400/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300 transition-colors duration-300 hover:bg-emerald-400/10 disabled:opacity-30"
              >
                {busy === r.id + 'confirmed' ? 'Saving…' : 'Confirm'}
              </button>
              <button
                data-testid={`reservation-decline-${r.id}`}
                onClick={() => setStatus(r.id, 'declined')}
                disabled={busy === r.id + 'declined' || r.status === 'declined'}
                className="border border-red-400/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-red-300 transition-colors duration-300 hover:bg-red-400/10 disabled:opacity-30"
              >
                {busy === r.id + 'declined' ? 'Saving…' : 'Decline'}
              </button>
              {r.status !== 'pending' && (
                <button
                  data-testid={`reservation-reopen-${r.id}`}
                  onClick={() => setStatus(r.id, 'pending')}
                  className="px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 hover:text-gold"
                >
                  Reopen
                </button>
              )}
              <button
                data-testid={`reservation-delete-${r.id}`}
                onClick={() => remove(r.id)}
                disabled={busy === r.id + 'delete'}
                className="ml-auto px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] text-white/30 transition-colors duration-300 hover:text-red-400 disabled:opacity-30"
              >
                {busy === r.id + 'delete' ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </motion.article>
        ))}
      </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
  }, []);

  return (
    <div className="grain flex min-h-screen items-start justify-center bg-ink px-6 py-16 font-body text-white md:py-24">
      <div className="w-full max-w-6xl">
        <a data-testid="admin-back-link" href="/" className="mb-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </a>
        <div className={token ? '' : 'flex justify-center pt-10'}>
          {token ? <Dashboard token={token} onLogout={logout} /> : <LoginForm onLogin={setToken} />}
        </div>
      </div>
    </div>
  );
}
